import React, { useState, useEffect } from "react";
import { Modal, DatePicker, Input, Button, Divider, Tag } from "antd";
import { useDispatch } from "react-redux";
import { UpdateTimeslot, DisableWholeDay, EnableWholeDay } from "../../Redux/Actions/agentActions";

const { TextArea } = Input;

const DisableTimeslotModal = ({ visible, record, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [action, setAction] = useState("disable");
  const [dates, setDates] = useState([]);
  const [reason, setReason] = useState("");
  const [wholeDayAction, setWholeDayAction] = useState("disable"); // "disable" | "enable"
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) {
      setAction("disable");
      setDates([]);
      setReason("");
      setWholeDayAction("disable");
    }
  }, [visible]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (action === "whole-day") {
        if (dates.length === 0) return;
        for (const d of dates) {
          const formattedDate = d.format("YYYY-MM-DD");
          if (wholeDayAction === "disable") {
            await dispatch(DisableWholeDay(formattedDate, reason || null));
          } else {
            await dispatch(EnableWholeDay(formattedDate));
          }
        }
      } else if (action === "disable") {
        if (dates.length === 0) return;
        for (const d of dates) {
          const payload = {
            id: record?.id,
            action,
            date: d.format("YYYY-MM-DD"),
            reason: reason || null,
            disabled_by: "admin",
          };
          await dispatch(UpdateTimeslot(payload));
        }
      } else if (action === "enable") {
        if (dates.length === 0) return;
        for (const d of dates) {
          const payload = {
            id: record?.id,
            action,
            date: d.format("YYYY-MM-DD"),
          };
          await dispatch(UpdateTimeslot(payload));
        }
      } else if (action === "toggle") {
        const payload = { id: record?.id, available: !record?.available };
        await dispatch(UpdateTimeslot(payload));
      }

      onSuccess?.();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const isDateRequired = action === "disable" || action === "enable" || action === "whole-day";
  const isSubmitDisabled = isDateRequired && dates.length === 0;

  const actionColors = {
    disable: "#ff4d4f",
    enable: "#52c41a",
    toggle: "#1677ff",
    "whole-day": "#fa8c16",
  };

  const actionLabels = {
    disable: "Disable Slot",
    enable: "Enable Slot",
    toggle: record?.available ? "Mark Unavailable" : "Mark Available",
    "whole-day": wholeDayAction === "disable" ? "Disable Whole Day" : "Enable Whole Day",
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={480}
      centered
      closable={false}
      destroyOnClose
      styles={{ body: { padding: "0" } }}
      title={null}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)",
          borderRadius: "8px 8px 0 0",
          padding: "24px 28px 20px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
        <div style={{ position: "absolute", bottom: -20, left: 60, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
        <p style={{ color: "rgba(255,255,255,0.5)", margin: "0 0 2px", fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>
          Manage Time Slot
        </p>
        <h2 style={{ color: "#fff", margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: 0.3 }}>
          Update Slot
        </h2>
        {record && (
          <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", borderRadius: 20, padding: "4px 14px" }}>
            <span style={{ fontSize: 16 }}>🕐</span>
            <span style={{ color: "#e0e0e0", fontWeight: 600, fontSize: 14 }}>{record.time}</span>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: record.available ? "#52c41a" : "#ff4d4f", display: "inline-block" }} />
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
              {record.available ? "Available" : "Unavailable"}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "24px 28px 28px" }}>

        {/* Action Selector */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", marginBottom: 10, fontWeight: 600, color: "#374151", fontSize: 13 }}>
            Select Action
          </label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              { key: "disable", label: "🚫 Disable", colors: { bg: "#fff1f0", border: "#ff4d4f", text: "#ff4d4f" } },
              { key: "enable", label: "✅ Enable", colors: { bg: "#f6ffed", border: "#52c41a", text: "#52c41a" } },
              { key: "toggle", label: "🔄 Toggle", colors: { bg: "#e6f4ff", border: "#1677ff", text: "#1677ff" } },
              { key: "whole-day", label: "📅 Whole Day", colors: { bg: "#fff7e6", border: "#fa8c16", text: "#fa8c16" } },
            ].map(({ key, label, colors }) => {
              const isActive = action === key;
              return (
                <button
                  key={key}
                  onClick={() => { setAction(key); setDates([]); setReason(""); }}
                  style={{
                    flex: 1,
                    minWidth: "40%",
                    padding: "10px 4px",
                    border: `1.5px solid ${isActive ? colors.border : "#e5e7eb"}`,
                    borderRadius: 8,
                    background: isActive ? colors.bg : "#fafafa",
                    color: isActive ? colors.text : "#9ca3af",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 12,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    letterSpacing: 0.3,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <Divider style={{ margin: "0 0 20px", borderColor: "#f0f0f0" }} />

        {/* Whole Day Sub-action toggle */}
        {action === "whole-day" && (
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#374151", fontSize: 13 }}>
              Whole Day Action
            </label>
            <div style={{ display: "flex", gap: 10 }}>
              {[
                { key: "disable", label: "🚫 Disable Day", colors: { bg: "#fff1f0", border: "#ff4d4f", text: "#ff4d4f" } },
                { key: "enable", label: "✅ Enable Day", colors: { bg: "#f6ffed", border: "#52c41a", text: "#52c41a" } },
              ].map(({ key, label, colors }) => {
                const isActive = wholeDayAction === key;
                return (
                  <button
                    key={key}
                    onClick={() => setWholeDayAction(key)}
                    style={{
                      flex: 1,
                      padding: "10px 4px",
                      border: `1.5px solid ${isActive ? colors.border : "#e5e7eb"}`,
                      borderRadius: 8,
                      background: isActive ? colors.bg : "#fafafa",
                      color: isActive ? colors.text : "#9ca3af",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 12,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Date Picker */}
        {isDateRequired && (
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#374151", fontSize: 13 }}>
              Select Dates <span style={{ color: "#ff4d4f" }}>*</span>
            </label>

            <DatePicker
              style={{ width: "100%", height: 40, borderRadius: 8 }}
              value={null}
              onChange={(val) => {
                if (val && !dates.find((d) => d.isSame(val, "day"))) {
                  setDates([...dates, val]);
                }
              }}
              disabledDate={(current) =>
                (current && current.valueOf() < Date.now() - 86400000) ||
                dates.some((d) => d.isSame(current, "day"))
              }
              placeholder="Pick dates one by one"
            />

            {/* Selected date tags */}
            {dates.length > 0 && (
              <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
                {dates.map((d, i) => (
                  <Tag
                    key={i}
                    closable
                    onClose={() => setDates(dates.filter((_, idx) => idx !== i))}
                    style={{
                      borderRadius: 6,
                      padding: "3px 10px",
                      fontSize: 12,
                      fontWeight: 500,
                      background: "#f0f9ff",
                      border: "1px solid #bae6fd",
                      color: "#0369a1",
                    }}
                  >
                    {d.format("DD MMM YYYY")}
                  </Tag>
                ))}
                <Tag
                  onClick={() => setDates([])}
                  style={{
                    borderRadius: 6,
                    padding: "3px 10px",
                    fontSize: 12,
                    cursor: "pointer",
                    background: "#fff1f0",
                    border: "1px solid #ffccc7",
                    color: "#ff4d4f",
                  }}
                >
                  Clear All
                </Tag>
              </div>
            )}
          </div>
        )}

        {/* Reason — for disable slot or disable whole day */}
        {(action === "disable" || (action === "whole-day" && wholeDayAction === "disable")) && (
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#374151", fontSize: 13 }}>
              Reason <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
            </label>
            <TextArea
              rows={3}
              placeholder="e.g. Maintenance, Holiday, Staff leave..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ borderRadius: 8, resize: "none" }}
            />
          </div>
        )}

        {/* Toggle info */}
        {action === "toggle" && (
          <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "12px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>ℹ️</span>
            <span style={{ fontSize: 13, color: "#0369a1", lineHeight: 1.5 }}>
              This will mark the slot as <strong>{record?.available ? "Unavailable" : "Available"}</strong> globally (no date restriction).
            </span>
          </div>
        )}

        {/* Whole Day info */}
        {action === "whole-day" && (
          <div style={{ background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 8, padding: "12px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <span style={{ fontSize: 13, color: "#d46b08", lineHeight: 1.5 }}>
              This will <strong>{wholeDayAction === "disable" ? "disable" : "enable"}</strong> <strong>all time slots</strong> for the selected date.
            </span>
          </div>
        )}

        {/* Footer Buttons */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
          <Button onClick={onClose} style={{ borderRadius: 8, height: 40, paddingInline: 20, fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
            style={{
              borderRadius: 8,
              height: 40,
              paddingInline: 24,
              fontWeight: 600,
              background: isSubmitDisabled ? undefined : actionColors[action],
              borderColor: isSubmitDisabled ? undefined : actionColors[action],
            }}
          >
            {actionLabels[action]}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DisableTimeslotModal;